import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import { Menu, X, ChevronDown } from 'lucide-react';
// import ThemeToggle from './ThemeToggle'; // Temporarily disabled for light mode only

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMobileMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleMenu();
    }
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent, itemLabel: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setActiveDropdown(activeDropdown === itemLabel ? null : itemLabel);
    } else if (event.key === 'Escape') {
      setActiveDropdown(null);
    } else if (event.key === 'ArrowDown' && activeDropdown === itemLabel) {
      event.preventDefault();
      // Focus first dropdown item
      const dropdown = dropdownRef.current?.querySelector(`[data-dropdown="${itemLabel}"]`);
      const firstLink = dropdown?.querySelector('a') as HTMLAnchorElement;
      firstLink?.focus();
    }
  };

  const handleDropdownItemKeyDown = (event: React.KeyboardEvent, isLast: boolean, isFirst: boolean) => {
    if (event.key === 'ArrowDown' && !isLast) {
      event.preventDefault();
      const nextElement = (event.target as HTMLElement).parentElement?.nextElementSibling?.querySelector('a') as HTMLAnchorElement;
      nextElement?.focus();
    } else if (event.key === 'ArrowUp' && !isFirst) {
      event.preventDefault();
      const prevElement = (event.target as HTMLElement).parentElement?.previousElementSibling?.querySelector('a') as HTMLAnchorElement;
      prevElement?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setActiveDropdown(null);
      // Focus back to the dropdown button
      const button = dropdownRef.current?.querySelector('button[aria-expanded="true"]') as HTMLButtonElement;
      button?.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { 
      label: 'Services', 
      dropdown: [
        { path: '/services', label: 'All Services' },
        { path: '/services/ai-implementation', label: 'AI Implementation' },
        { path: '/services/cloud-security', label: 'Cloud Security' },
        { path: '/services/compliance', label: 'Compliance' },
      ]
    },
    {
      label: 'Industries',
      dropdown: [
        { path: '/industries/healthcare', label: 'Healthcare' },
        { path: '/industries/legal', label: 'Legal' },
        { path: '/industries/manufacturing', label: 'Manufacturing' },
      ]
    },
    {
      label: 'Resources',
      dropdown: [
        { path: '/resources/ai-case-studies', label: 'Case Studies' },
        { path: '/resources/ai-tools', label: 'AI Tools' },
        { path: '/resources/ai-guides', label: 'Implementation Guides' },
      ]
    },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <nav className="sticky top-0 z-40 bg-navbar-bg/80 backdrop-blur-md border-b border-primary/20 shadow-lg text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" aria-label="Menashi - Home" className="flex items-center gap-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <BrandLogo src="/branding/menashi-logo.svg" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1" ref={dropdownRef}>
            {navItems.map((item, index) => (
              <div key={item.path || item.label} className="relative">
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                      onKeyDown={(e) => handleDropdownKeyDown(e, item.label)}
                      className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 menashi-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                        item.dropdown.some(subItem => location.pathname === subItem.path)
                          ? 'text-text-light bg-primary/20 border-primary/50'
                          : 'text-text-muted hover:text-text-light hover:bg-primary/10 hover:border-primary/30'
                      }`}
                      aria-expanded={activeDropdown === item.label}
                      aria-haspopup="true"
                    >
                      {item.label}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === item.label ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {activeDropdown === item.label && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-primary/20 rounded-lg shadow-lg py-2 z-50" data-dropdown={item.label}>
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-2 text-sm transition-all duration-200 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                              location.pathname === subItem.path
                                ? 'text-primary bg-primary/10'
                                : 'text-card-foreground hover:text-primary hover:bg-primary/5'
                            }`}
                            onClick={() => setActiveDropdown(null)}
                            onKeyDown={(e) => handleDropdownItemKeyDown(e, subIndex === item.dropdown.length - 1, subIndex === 0)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path!}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 menashi-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      location.pathname === item.path
                        ? 'text-text-light bg-primary/20 border-primary/50'
                        : 'text-text-muted hover:text-text-light hover:bg-primary/10 hover:border-primary/30'
                    }`}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            {/* <ThemeToggle /> - Temporarily disabled for light mode only */}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={handleToggleMenu}
              onKeyDown={handleMobileMenuKeyDown}
              className="inline-flex items-center justify-center p-2 rounded-lg text-text-muted hover:text-text-light hover:bg-primary/10 menashi-border focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-4 pt-2 pb-3 space-y-2 bg-secondary backdrop-blur-md border-t border-primary/20">
            {navItems.map((item) => (
              <div key={item.path || item.label}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                      onKeyDown={(e) => handleDropdownKeyDown(e, item.label)}
                      className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 menashi-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                        item.dropdown.some(subItem => location.pathname === subItem.path)
                          ? 'text-text-light bg-primary/20 border-primary/50'
                          : 'text-text-muted hover:text-text-light hover:bg-primary/10 hover:border-primary/30'
                      }`}
                      aria-expanded={activeDropdown === item.label}
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === item.label ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {activeDropdown === item.label && (
                      <div className="mt-2 ml-4 space-y-1">
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-2 rounded-lg text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                              location.pathname === subItem.path
                                ? 'text-primary bg-primary/10'
                                : 'text-text-muted hover:text-text-light hover:bg-primary/5'
                            }`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                            }}
                            onKeyDown={(e) => handleDropdownItemKeyDown(e, subIndex === item.dropdown.length - 1, subIndex === 0)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path!}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 menashi-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      location.pathname === item.path
                        ? 'text-text-light bg-primary/20 border-primary/50'
                        : 'text-text-muted hover:text-text-light hover:bg-primary/10 hover:border-primary/30'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </nav>
    </>
  );
};

export default Navigation; 